import { namespace as axiosRetryNamespace } from 'axios-retry';
import apiClient from './api.service';

type DeliveryCitiesPayload = { cities: any[]; count: number; cities_version?: string | null };

/**
 * Delivery Service
 * Handles all API calls related to delivery integrations
 */
class DeliveryService {
  private readonly deliveryCitiesTtlMs = 60 * 60 * 1000;
  private deliveryCitiesCache = new Map<string, { expiry: number; data: DeliveryCitiesPayload }>();
  private deliveryCitiesInflight = new Map<string, Promise<DeliveryCitiesPayload>>();

  private deliveryCitiesCacheKey(storeId: number, integrationKey: string) {
    return `${storeId}:${integrationKey}`;
  }

  /** Call after sync-cities so the next getCities refetches from the API. */
  invalidateDeliveryCitiesCache(storeId: number, integrationKey: string): void {
    this.deliveryCitiesCache.delete(this.deliveryCitiesCacheKey(storeId, integrationKey));
  }

  /** Invalide toutes les listes de villes en cache pour un store (ex. après sync super-admin). */
  invalidateDeliveryCitiesCacheForStore(storeId: number): void {
    const prefix = `${storeId}:`;
    Array.from(this.deliveryCitiesCache.keys()).forEach((key) => {
      if (key.startsWith(prefix)) {
        this.deliveryCitiesCache.delete(key);
      }
    });
  }

  /**
   * Warms the in-memory cities cache without blocking the main thread.
   * Staggers requests so several integrations do not hit the network at once.
   */
  prefetchDeliveryCitiesForStore(storeId: number, integrationKeys: string[]): void {
    const seen = new Set<string>();
    const unique: string[] = [];
    integrationKeys.forEach((k) => {
      if (k && !seen.has(k)) {
        seen.add(k);
        unique.push(k);
      }
    });
    if (unique.length === 0 || typeof window === 'undefined') return;
    const run = () => {
      unique.forEach((key, index) => {
        window.setTimeout(() => {
          this.getCities(storeId, key).catch(() => {});
        }, index * 450);
      });
    };
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof ric === 'function') {
      ric(() => run(), { timeout: 5000 });
    } else {
      window.setTimeout(run, 800);
    }
  }

  private async fetchCitiesFromApi(
    storeId: number,
    integrationKey: string,
    search?: string,
    limit?: number
  ): Promise<DeliveryCitiesPayload> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (limit != null) params.append('limit', String(limit));
    const response = await apiClient.get<{ success: boolean; data: DeliveryCitiesPayload }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/cities?${params.toString()}`
    );
    return response.data.data;
  }
  /**
   * Configure delivery integration credentials (Customer ID + API Key)
   */
  async configureCredentials(
    storeId: number,
    integrationKey: string,
    customerId: string,
    apiKey: string
  ): Promise<any> {
    const response = await apiClient.post<{ success: boolean; data: any; message: string }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/configure`,
      { customer_id: customerId, api_key: apiKey }
    );
    return response.data;
  }

  /**
   * Get delivery field mappings
   */
  async getMappings(storeId: number, integrationKey: string): Promise<any> {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/mappings`
    );
    return response.data.data;
  }

  /**
   * Save delivery field mappings
   */
  async saveMappings(storeId: number, integrationKey: string, mappings: any[]): Promise<any> {
    const response = await apiClient.post<{ success: boolean; data: any; message: string }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/mappings`,
      { mappings }
    );
    return response.data;
  }

  /**
   * Synchronize cities from delivery integration
   */
  async syncCities(storeId: number, integrationKey: string): Promise<any> {
    const response = await apiClient.post<{ success: boolean; data: any; message: string }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/sync-cities`
    );
    return response.data;
  }

  /**
   * Full list (no search/limit) cached 5 min (invalidation manuelle ou cities_version).
   */
  async getCities(storeId: number, integrationKey: string, search?: string, limit?: number): Promise<any> {
    const useCache = !search && limit == null;
    if (!useCache) {
      return this.fetchCitiesFromApi(storeId, integrationKey, search, limit);
    }
    const key = this.deliveryCitiesCacheKey(storeId, integrationKey);
    const now = Date.now();
    const hit = this.deliveryCitiesCache.get(key);
    if (hit && hit.expiry > now) {
      return hit.data;
    }
    let inflight = this.deliveryCitiesInflight.get(key);
    if (!inflight) {
      inflight = this.fetchCitiesFromApi(storeId, integrationKey)
        .then((data) => {
          this.deliveryCitiesInflight.delete(key);
          this.deliveryCitiesCache.set(key, { expiry: Date.now() + this.deliveryCitiesTtlMs, data });
          return data;
        })
        .catch((err) => {
          this.deliveryCitiesInflight.delete(key);
          throw err;
        });
      this.deliveryCitiesInflight.set(key, inflight);
    }
    return inflight;
  }

  /**
   * Get all delivery integrations for a store
   */
  async getStoreDeliveryIntegrations(storeId: number): Promise<any[]> {
    const response = await apiClient.get<{ success: boolean; data: { integrations: any[] } }>(
      `/delivery/store/${storeId}/integrations`
    );
    return response.data.data.integrations;
  }

  /**
   * Bulk preview: multiple orders in one request (faster than N single previews).
   */
  async bulkPreviewOrdersForDelivery(
    storeId: number,
    orderIds: number[],
    deliveryIntegrationId: number
  ): Promise<Record<number, any>> {
    const response = await apiClient.post<{ success: boolean; data: { previews: Record<number, any> } }>(
      `/delivery/store/${storeId}/orders/bulk-preview`,
      { orderIds, deliveryIntegrationId }
    );
    return response.data.data.previews || {};
  }

  /**
   * Preview order data before sending to delivery (validation and preview).
   * When overrides is provided, the backend merges them into parcel_data and re-validates (field_errors, field_warnings, can_send).
   */
  async previewOrderForDelivery(
    storeId: number,
    orderId: number,
    deliveryIntegrationId: number,
    overrides?: Record<string, any>
  ): Promise<any> {
    const url =
      overrides && Object.keys(overrides).length > 0
        ? `/delivery/store/${storeId}/orders/${orderId}/preview/${deliveryIntegrationId}?overrides=${encodeURIComponent(JSON.stringify(overrides))}`
        : `/delivery/store/${storeId}/orders/${orderId}/preview/${deliveryIntegrationId}`;
    const response = await apiClient.get<{ success: boolean; data: any }>(url);
    return response.data.data;
  }

  /**
   * Update an order field for delivery. The response includes a rebuilt preview when available.
   */
  async updateOrderDeliveryField(
    storeId: number,
    orderId: number,
    deliveryIntegrationId: number,
    payload: { fieldKey: string; value: any }
  ): Promise<{ updated: boolean; preview?: any | null }> {
    const response = await apiClient.patch<{ success: boolean; data: { updated: boolean; preview?: any | null } }>(
      `/delivery/store/${storeId}/orders/${orderId}/field`,
      { deliveryIntegrationId, fieldKey: payload.fieldKey, value: payload.value }
    );
    return response.data.data;
  }

  /**
   * Send an order to a delivery company
   */
  async sendOrderToDelivery(storeId: number, orderId: number, deliveryIntegrationId: number, overrides?: Record<string, any>): Promise<any> {
    const response = await apiClient.post<{ success: boolean; data: any; message: string }>(
      `/delivery/store/${storeId}/orders/${orderId}/send`,
      { deliveryIntegrationId, overrides: overrides || undefined }
    );
    return response.data;
  }

  /**
   * Refresh tracking status for an order
   */
  async refreshTrackingStatus(storeId: number, orderId: number): Promise<any> {
    const response = await apiClient.post<{ success: boolean; data: any; message: string }>(
      `/delivery/store/${storeId}/orders/${orderId}/refresh-tracking`
    );
    return response.data;
  }

  /**
   * Get tracking timeline for an order.
   * Shorter timeout than the global client default so the UI fails fast if the backend/Ozone is slow;
   * disables axios-retry here to avoid doubling load on an already heavy endpoint.
   */
  async getOrderTracking(storeId: number, orderId: number): Promise<any> {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/delivery/store/${storeId}/orders/${orderId}/tracking`,
      {
        timeout: 22000,
        [axiosRetryNamespace]: { retries: 0 }
      }
    );
    return response.data.data;
  }

  /**
   * Get delivery history for a store integration
   */
  async getDeliveryHistory(
    storeId: number,
    integrationKey: string,
    options?: { limit?: number; offset?: number; action?: string; date_from?: string; date_to?: string }
  ): Promise<{ logs: any[]; pagination: any }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    if (options?.action) params.append('action', options.action);
    if (options?.date_from) params.append('date_from', options.date_from);
    if (options?.date_to) params.append('date_to', options.date_to);

    const response = await apiClient.get<{ success: boolean; data: { logs: any[]; pagination: any } }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/history?${params.toString()}`
    );
    return response.data.data;
  }

  /**
   * Get city field mapping info (which custom field is mapped to parcel-city and available cities)
   */
  async getCityFieldMapping(storeId: number): Promise<{ mappings: any[] }> {
    const response = await apiClient.get<{ success: boolean; data: { mappings: any[] } }>(
      `/delivery/store/${storeId}/city-field-mapping`
    );
    return response.data.data;
  }

  /**
   * Get webhook URL for a delivery integration
   */
  async getWebhookUrl(storeId: number, integrationKey: string): Promise<{ webhook_url: string; webhook_registered_at: string | null; store_integration_id: number; webhook_token_configured?: boolean; webhook_token_mode?: string }> {
    const response = await apiClient.get<{ success: boolean; data: { webhook_url: string; webhook_registered_at: string | null; store_integration_id: number; webhook_token_configured?: boolean; webhook_token_mode?: string } }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/webhook-url`
    );
    return response.data.data;
  }

  /**
   * Generate (or rotate) the secret Ozon webhook token. The tokenized URL is returned only once.
   */
  async generateWebhookToken(storeId: number, integrationKey: string): Promise<{ webhook_url: string; legacy_webhook_url: string; store_integration_id: number }> {
    const response = await apiClient.post<{ success: boolean; data: { webhook_url: string; legacy_webhook_url: string; store_integration_id: number } }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/webhook-token`
    );
    return response.data.data;
  }

  /**
   * Resolve an order for the Ozone Express webhook test panel
   */
  async resolveWebhookTestOrder(
    storeId: number,
    integrationKey: string,
    orderId: string
  ): Promise<{
    order: {
      id: number;
      order_number: string;
      delivery_status: string | null;
      delivery_tracking_number: string | null;
      delivery_situation: string | null;
      delivery_company: string | null;
      delivery_integration_id: number | null;
    };
    webhook_url: string;
    store_integration_id: number;
  }> {
    const params = new URLSearchParams({ order_id: orderId });
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/webhook-test/order?${params.toString()}`
    );
    return response.data.data;
  }

  /**
   * Simulate an Ozone Express webhook for a store order
   */
  async simulateWebhookTest(
    storeId: number,
    integrationKey: string,
    payload: {
      order_id: string;
      orderStatus?: string;
      situation?: string;
      note?: string;
      tracking_number?: string;
      payload?: Record<string, unknown>;
    }
  ): Promise<any> {
    const response = await apiClient.post<{ success: boolean; data: any }>(
      `/delivery/store/${storeId}/integration/${integrationKey}/webhook-test`,
      payload
    );
    return response.data.data;
  }
}

export default new DeliveryService();

